
import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { ParticipanteForm } from "@/types/participante";
import { adicionarConfirmado } from "@/lib/participantes";
import { mascaraCPF, mascaraTelefone, apenasDigitos } from "@/lib/masks";

const inputCls =
  "w-full px-3.5 py-2.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black placeholder-black/25 outline-none focus:border-black focus:bg-white transition";

interface Props {
  edicaoId: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function AdicionarParticipanteModal({ edicaoId, onClose, onSaved }: Props) {
  const [form, setForm] = useState<ParticipanteForm>({
    nomeCompleto: "",
    cpf: "",
    email: "",
    whatsapp: "",
  });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const set = (key: keyof ParticipanteForm, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");

    const cpfLimpo = apenasDigitos(form.cpf);
    const whatsappLimpo = apenasDigitos(form.whatsapp);

    if (cpfLimpo.length !== 11) {
      setErro("CPF inválido. Informe os 11 dígitos.");
      return;
    }
    if (whatsappLimpo.length < 10) {
      setErro("WhatsApp inválido. Informe o DDD + número.");
      return;
    }

    setLoading(true);
    try {
      await adicionarConfirmado(
        edicaoId,
        { ...form, cpf: cpfLimpo, whatsapp: whatsappLimpo },
        "manual"
      );
      onSaved();
    } catch {
      setErro("Erro ao salvar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/5">
          <h2 className="text-base font-semibold text-black">Adicionar Confirmado</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-black hover:bg-black/5 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form id="form-participante" onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Nome completo</label>
            <input
              required
              type="text"
              placeholder="João da Silva"
              value={form.nomeCompleto}
              onChange={(e) => {
                const valor = e.target.value;
                // Remove números do valor
                const valorSemNumeros = valor.replace(/[0-9]/g, '');
                set("nomeCompleto", valorSemNumeros);
              }}
              className={inputCls}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">CPF</label>
            <input
              required
              type="text"
              placeholder="000.000.000-00"
              value={form.cpf}
              onChange={(e) => set("cpf", mascaraCPF(e.target.value))}
              maxLength={14}
              className={inputCls}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">E-mail</label>
            <input
              type="email"
              placeholder="email@exemplo.com"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">WhatsApp</label>
            <input
              required
              type="text"
              placeholder="(11) 99999-9999"
              value={form.whatsapp}
              onChange={(e) => set("whatsapp", mascaraTelefone(e.target.value))}
              maxLength={16}
              className={inputCls}
            />
          </div>
          {erro && (
            <p className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
              {erro}
            </p>
          )}
        </form>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-black/5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-black/50 hover:text-black hover:bg-black/5 transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="form-participante"
            disabled={loading}
            className="px-5 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : (
              "Adicionar"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
